import React, { FC } from 'react';
import { Button, Drawer, Form, InputNumber } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { Notification } from 'common/components';
import { objectIsEmpty, rules, showSuccessMessage } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import { DEFAULT_VALUE_0 } from 'common/config';
import { IFormValue } from 'common/models';
import { ArrowLeftLong, Close } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryAssigneeSelector, InventoryAvailableCount } from 'entities/Inventory/components';
import { inventoryCount } from 'entities/Inventory/Inventory.helper';
import { GoodsCardMin } from 'entities/Goods/components';
import {
  addInventoryAssignee,
  selectInventory,
  selectInventoryAssignees,
  selectIsInventoryLoading,
} from 'entities/Inventory/Inventory.slice';

export const InventoryAssignCard: FC = () => {
  const dispatch = useAppDispatch();
  // inventory
  const inventory = useAppSelector(selectInventory);
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);
  const inventoryAssignees = useAppSelector(selectInventoryAssignees);

  const [form] = useForm();
  const formValues = useWatch([], form);
  const { isNestedCard, setIsNestedCard, openInventoryAssigneeCard, setOpenInventoryAssigneeCard } = useInventoryContext();

  const availableCount = inventoryCount.availableCount(inventory);
  const submitDisabled = formValues && (objectIsEmpty(formValues) || Object.values(formValues).some((value) => !value));

  const onCancel = () => {
    if (isInventoryLoading) {
      return;
    }

    setOpenInventoryAssigneeCard(false);

    setIsNestedCard(false);
    form.resetFields();
  };

  const onSubmit = ({ assigneeId, count }: IFormValue) => {
    if (inventory) {
      const selectedAssignee = inventoryAssignees.find((assignee) => assignee.id === assigneeId);
      const inventoryAssignee = inventory.assignees.find((assignee) => assignee.id === assigneeId);

      dispatch(
        addInventoryAssignee({
          inventoryId: inventory.id,
          assigneeId,
          count: inventoryAssignee ? inventoryAssignee.count + count : count,
          onSuccess: () => {
            setOpenInventoryAssigneeCard(false);
            setIsNestedCard(false);
            form.resetFields();
            showSuccessMessage(`Оборудование ${count} шт. закреплено за сотрудником ${selectedAssignee?.name}.`);
          },
        }),
      );
    }
  };

  return (
    <Drawer
      rootClassName={`redesign drawer ${isNestedCard ? 'nested' : ''}`}
      open={openInventoryAssigneeCard}
      onClose={onCancel}
      width={800}
      destroyOnClose
      closeIcon={isNestedCard ? <ArrowLeftLong /> : <Close />}
      classNames={{ footer: 'flex align-center justify-between' }}
      footer={[
        <Button key="cancel" className="button-lg secondary" onClick={onCancel} loading={isInventoryLoading}>
          Отмена
        </Button>,
        <Button
          key="confirm"
          className="button-lg primary"
          onClick={form.submit}
          loading={isInventoryLoading}
          disabled={submitDisabled}
        >
          Подтвердить
        </Button>,
      ]}
    >
      <div className="drawer__body">
        <div className="ff-regular fs-xxl mb-14">Закрепить инструмент за другим сотрудником</div>

        <div className="mb-6">
          <GoodsCardMin good={inventory?.good} />
        </div>

        {inventory?.count && (
          <div className="mb-9">
            <InventoryAvailableCount availableCount={availableCount} totalCount={inventory.count} />
          </div>
        )}

        <Form layout="vertical" requiredMark={false} form={form} onFinish={onSubmit}>
          <Form.Item className="mb-9" label="Закрепить за*" name="assigneeId" rules={[rules.required()]}>
            <InventoryAssigneeSelector assignees={inventoryAssignees} />
          </Form.Item>

          <Form.Item
            className="mb-9"
            label="Количество оборудования*"
            name="count"
            rules={[
              rules.required(),
              () => ({
                validator(_, value) {
                  if (value > availableCount || value <= DEFAULT_VALUE_0) {
                    return Promise.reject(new Error('Невозможно указать введённое количество.'));
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber controls={false} suffix="шт" />
          </Form.Item>
        </Form>

        <div className="mb-9">
          <Notification
            type="success"
            title="Закрепление инструмента за сотрудником, которого нет в системе"
            description="Вы можете указать ФИО сотрудника, который не зарегистрирован в системе, и закрепить за ним инструмент. При этом инструмент останется на вашем балансе."
          />
        </div>
      </div>
    </Drawer>
  );
};
